import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createDecoRouter } from "@decocms/tanstack";
import { routeTree } from "./routeTree.gen";
import { RootErrorFallback } from "./components/ui/RootErrorFallback";
import { applyAsyncRenderingAndConventions, applyLayoutSections } from "./setup";

// `setup.ts` só DEFINE estas funções agora (não roda nada no import) — ver o
// cabeçalho do arquivo para o bug real que essa mudança corrige (ordem
// invertida quebrando as sections globais de modal). Chamadas na mesma
// ordem relativa que o servidor usa (`setup/server.ts`); a ordem ENTRE as
// duas não importa para o cliente (não há commerce loaders aqui).
applyAsyncRenderingAndConventions();
applyLayoutSections();

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000 } },
});

export function getRouter() {
  const router = createDecoRouter({
    routeTree,
    context: { queryClient },
    Wrap: ({ children }) => (
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    ),
  });

  // Scroll restoration resets the window with
  // `window.scrollTo({ top: 0, left: 0, behavior })`. With `behavior`
  // unset the CSS `html { scroll-behavior: smooth }` from app.css takes
  // over, and on these pages (long PDPs, `content-visibility: auto`
  // sections re-laying out mid-navigation) the smooth animation is
  // cancelled before it moves a pixel — so the reset silently no-ops and
  // the next PDP renders at the previous page's scroll offset.
  // Force the instant jump the original (full page load) had.
  router.options.scrollRestorationBehavior = "instant";

  // Floor for any route without its own `errorComponent`: an uncaught throw
  // used to bubble to the top and render a blank page. `CreateDecoRouterOptions`
  // doesn't expose this option, so it's set post-construction. See
  // ~/components/ui/RootErrorFallback.
  router.options.defaultErrorComponent = RootErrorFallback;

  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
